import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import { OverlayTrigger, Tooltip, Button, Modal, Form } from "react-bootstrap";
import InfoIcon from "../../assets/img/info-user-icon.png";
import editIcon from "../../assets/img/edit-icon.png";
import crossIcon from "../../assets/img/cross-icon.png";
import { useDispatch, useSelector } from "react-redux";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useForm } from "react-hook-form";
import { fetchSupportConfig } from "../../redux/slice/supportConfigSlice";
import { createSupportConfig } from "../../redux/slice/supportConfigSlice";
import { format } from "date-fns";

function SupportConfig() {
    const dispatch = useDispatch();
    const { register, handleSubmit, reset, formState: { errors } } =
        useForm();

    const supportConfigKeys = [{ label: "Profile Update Window Days", value: "PROFILE_UPDATE_WINDOW_DAYS" }, { label: "Max Attachment Size (MB)", value: "MAX_ATTACHMENT_SIZE_MB" }, { label: "SLA Breach Days", value: "SLA_BREACH_DAYS" }, { label: "Reminder Before Deadline Days", value: "REMINDER_BEFORE_DEADLINE_DAYS" }]

    const [configUpdateId, setConfigUpdateId] = useState(null);
    const [showConfigUpdateModal, setShowConfigUpdateModal] = useState(false);
    const [editConfig, setEditConfig] = useState({
        name: "",
        value: "",
        desc: "",
    });
    const { supportConfig, configStatus } = useSelector((state) => state.supportConfig);
    const canView = useHasPermission("Settings-view");
    const canEdit = useHasPermission("Settings-manage");

    useEffect(() => {
        if (canView) {
            dispatch(fetchSupportConfig());
        }
    }, [dispatch, canView]);

    if (!canView) {
        return (
            <p className="perm-msg">
                You don’t have permission to view settings page!
            </p>
        );
    }

    const handleEditConfig = (config) => {
        setConfigUpdateId(config.id);
        setEditConfig({
            name: config.configKey,
            displayName: config.configKey?.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
            value: config.configValue,
            desc: config.description,
        });
        setShowConfigUpdateModal(true);
    };

    const handleUpdateConfig = async () => {
        if (!canEdit) {
            toast.error("You don't have permission to edit configuration", {
                className: "toast-error-custom",
                autoClose: 5000,
            });
            return;
        }
        if (configUpdateId === null) return;
        try {
            const payload = {
                configKey: editConfig.name,
                configValue: editConfig.value,
                description: editConfig.desc,
            };

            await dispatch(createSupportConfig(payload)).unwrap();
            setShowConfigUpdateModal(false);
            await dispatch(fetchSupportConfig()).unwrap();
            setConfigUpdateId(null);
            toast.success("Configuration updated successfully!");
        } catch (error) {
            setShowConfigUpdateModal(false);
            setConfigUpdateId(null);
            toast.error(error?.message || "Failed to update configuration!");
        }
    };

    const handleFormSubmit = async (data) => {
        if (!canEdit) {
            toast.error("You don't have permission to save configuration", {
                className: "toast-error-custom",
                autoClose: 5000,
            });
            return;
        }

        const payload = {
            configKey: data.configName,
            configValue: data.configValue,
            description: data.configDesc,
        };
        try {
            await dispatch(createSupportConfig(payload)).unwrap();
            toast.success("Configuration has been created sucessfully!", {
                className: "toast-success-custom",
            });
            await dispatch(fetchSupportConfig()).unwrap();
        } catch (err) {
            toast.error(err || "unable to connect to server!", {
                className: "toast-error-custom",
            });
        }
        reset();
    };

    const renderTooltip = (props, description) => (
        <Tooltip id="button-tooltip" {...props}>
            {description}
        </Tooltip>
    );

    return (
        <div>
            <div className="section-body m-0">
                <div className="container-fluid">
                    <div className="settings-heading pt-3 pt-md-0">
                        <h5>SUPPORT CONFIG</h5>
                        <p>Configure the operational benchmarks and service standards for the support module. This section allows us to define critical system deadlines to ensure all support activities are governed by consistent organizational rules and resolved within expected timeframes.</p>
                    </div>
                    <div className="row clearfix">
                        <div className="col-lg-12 col-md-12">
                            <div className="setting-container">
                                <ul className="list-group mb-3">
                                    <li className="list-group-item">
                                        <div className="media mb-0">
                                            <div className="config-form p-0">
                                                <form onSubmit={handleSubmit(handleFormSubmit)}>
                                                    <div>
                                                        <div className="input-text">
                                                            <p>
                                                                Support Config. Keys{" "}
                                                                <span style={{ color: "red" }}>*</span>
                                                            </p>
                                                            <div className="user-input-icons">
                                                                <select
                                                                    className="input-field form-select-custom custom-dropdown"
                                                                    disabled={!canEdit}
                                                                    {...register("configName", {
                                                                        required: "Please select a Configuration key",
                                                                    })}
                                                                >
                                                                    <option value="" hidden>
                                                                        Please select a Configuration key
                                                                    </option>

                                                                    {supportConfigKeys.map(configName => {
                                                                        return (<option key={configName.label} value={configName.value}>
                                                                            {configName.label}
                                                                        </option>)
                                                                    })
                                                                    }
                                                                </select>
                                                                {errors.configName && (
                                                                    <div className="userErrorMessage">
                                                                        {errors.configName.message}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="input-text">
                                                            <p>
                                                                Config. Value{" "}
                                                                <span style={{ color: "red" }}>*</span>
                                                            </p>
                                                            <div className="user-input-icons">
                                                                <input
                                                                    className="input-field"
                                                                    type="number"
                                                                    min="1"
                                                                    max="10"
                                                                    disabled={!canEdit}
                                                                    placeholder="Please enter the config. value"
                                                                    {...register("configValue", {
                                                                        required: "Please enter the config. value",
                                                                    })}
                                                                />
                                                                {errors.configValue && (
                                                                    <div className="userErrorMessage">
                                                                        {errors.configValue.message}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="input-text">
                                                            <p>
                                                                Description{" "}
                                                            </p>
                                                            <div className="user-input-icons">
                                                                <input
                                                                    className="input-field"
                                                                    type="text"
                                                                    disabled={!canEdit}
                                                                    placeholder="Please enter the description"
                                                                    {...register("configDesc", {
                                                                        //required: "Please enter the description",
                                                                        maxLength: {
                                                                            value: 50,
                                                                            message: "Description cannot exceed 50 characters",
                                                                        },
                                                                        minLength: {
                                                                            value: 5,
                                                                            message: "Description must be at least 5 characters",
                                                                        },
                                                                    },
                                                                    )}
                                                                />
                                                                {errors.configDesc && (
                                                                    <div className="userErrorMessage">
                                                                        {errors.configDesc.message}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-center mt-4">
                                                            <button
                                                                className="primary-btn"
                                                                type="submit"
                                                                title={
                                                                    canEdit
                                                                        ? "save configuration"
                                                                        : "No permission to save configuration"
                                                                }
                                                                disabled={!canEdit}
                                                            >
                                                                Save Configuration
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                            <ToastContainer />
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-12 col-md-12">
                            <div className="card">
                                <div className="card-header">
                                    <p className="card-title">
                                        <b>Configuration List</b>
                                    </p>
                                </div>
                                <div className="card-body">
                                    <div className="row clearfix">
                                        <div className="role-data table-responsive mt-0">
                                            {configStatus === "loading" && (
                                                <p className="loading">Loading...</p>
                                            )}

                                            {configStatus === "failed" && (
                                                <p className="server-error-msg">Server Error...</p>
                                            )}
                                            {configStatus == "succeeded" &&
                                                (supportConfig.length > 0 ? (
                                                    <table className="role-data-table table table-hover table-vcenter text-nowrap mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th className="table-row">Name</th>
                                                                <th className="table-row">Value</th>
                                                                <th className="table-row">Updated At</th>
                                                                <th className="table-row">Updated By</th>
                                                                <th className="table-row">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {supportConfig.map((config) => (
                                                                <tr key={config.id}>
                                                                    <td>
                                                                        {config.configKey
                                                                            ?.replace(/_/g, " ")
                                                                            .toLowerCase()
                                                                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                                                                        <OverlayTrigger
                                                                            placement="right"
                                                                            delay={{ show: 250, hide: 400 }}
                                                                            overlay={(props) =>
                                                                                renderTooltip(props, config.description)
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={InfoIcon}
                                                                                alt=""
                                                                                style={{
                                                                                    width: "15px",
                                                                                    marginLeft: "10px",
                                                                                }}
                                                                            />
                                                                        </OverlayTrigger>
                                                                    </td>
                                                                    <td>{config.configValue}</td>
                                                                    <td>
                                                                        {config.updatedAt ? format(new Date(config.updatedAt), "M/d/yyyy, h:mm a") : "---"}
                                                                    </td>
                                                                    <td>{config.updatedBy}</td>

                                                                    <td className="action-column">
                                                                        <button
                                                                            className="edit-role"
                                                                            title={
                                                                                canEdit
                                                                                    ? "edit configuration"
                                                                                    : "No permisssion to edit configuration"
                                                                            }
                                                                            disabled={!canEdit}
                                                                            onClick={
                                                                                canEdit
                                                                                    ? () => handleEditConfig(config)
                                                                                    : undefined
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={editIcon}
                                                                                alt="edit"
                                                                                style={{ opacity: canEdit ? 1 : 0.5 }}
                                                                            />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <p className="loading">No configuration record found</p>
                                                ))}
                                        </div>
                                    </div>
                                    {/* modal for update config */}
                                    <Modal
                                        show={showConfigUpdateModal}
                                        onHide={() => setShowConfigUpdateModal(false)}
                                    >
                                        <Modal.Header>
                                            <Modal.Title>Edit Config</Modal.Title>
                                            <button
                                                onClick={() => setShowConfigUpdateModal(false)}
                                                className="modal-close"
                                            >
                                                <img
                                                    src={crossIcon}
                                                    alt="Close"
                                                    style={{ width: "20px", height: "20px" }}
                                                />
                                            </button>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <Form>
                                                <Form.Group>
                                                    <Form.Label>Config. Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={editConfig.displayName || ""}
                                                        disabled
                                                        onChange={(e) =>
                                                            setEditConfig({
                                                                ...editConfig,
                                                                name: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Please enter the config. name"
                                                    />
                                                </Form.Group>
                                                <Form.Group className="mt-3">
                                                    <Form.Label>Config. Value </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={editConfig.value}
                                                        onChange={(e) =>
                                                            setEditConfig({
                                                                ...editConfig,
                                                                value: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Please enter the config. value"
                                                    />
                                                </Form.Group>
                                                <Form.Group className="mt-3">
                                                    <Form.Label>Config. Description </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={editConfig.desc}
                                                        onChange={(e) =>
                                                            setEditConfig({
                                                                ...editConfig,
                                                                desc: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Please enter the description"
                                                    />
                                                </Form.Group>
                                            </Form>
                                        </Modal.Body>
                                        <Modal.Footer>
                                            <Button
                                                variant="cancel-cta"
                                                onClick={() => setShowConfigUpdateModal(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="update-cta"
                                                disabled={!canEdit}
                                                onClick={canEdit ? handleUpdateConfig : undefined}
                                            >
                                                Update
                                            </Button>
                                        </Modal.Footer>
                                    </Modal>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SupportConfig
